import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { useSnackbar } from "notistack";

import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import FormModal from "../../components/common/FormModal";
import ViewModal from "../../components/common/ViewModal";

import {
  useListLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useBulkCreateLocationsMutation,
} from "../../features/masterdata/masterdataApi";

import type { Location, LocationCreate, LocationType } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const LOCATION_TYPES: { value: LocationType; label: string }[] = [
  { value: "hotel", label: "Hotel" },
  { value: "office", label: "Office" },
  { value: "stop", label: "Stop" },
  { value: "airport", label: "Airport" },
  { value: "port", label: "Port" },
  { value: "other", label: "Other" },
];

function typeLabel(t: string) {
  return LOCATION_TYPES.find((x) => x.value === t)?.label ?? t;
}

type LocationOrdering = "name" | "-name";

export default function LocationsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = usePersistedListFilters("locations", {
    type: "" as LocationType | "",
    city: "",
    country: "",
    search: "",
    ordering: "name" as LocationOrdering,
  });
  const { type, city, country, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = () => {
    setFilters((prev) => ({ ...prev, ordering: (prev.ordering === "name" ? "-name" : "name") as LocationOrdering }));
  };
  const sortIcon = () => (ordering === "name" ? " ↑" : " ↓");

  // dialogs
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState<Location | null>(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);

  const [openBulk, setOpenBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");

  // form state
  const [form, setForm] = useState<LocationCreate>({
    name: "",
    type: "hotel",
    city: null,
    country: null,
    address_line1: null,
    region: null,
    lat: null,
    lng: null,
    notes: null,
  });

  // reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [type, city, country, debouncedSearch, ordering]);

  const { data, isLoading, isFetching, error } = useListLocationsQuery({
    type: type || undefined,
    city: city || undefined,
    country: country || undefined,
    search: debouncedSearch,
    ordering,
    page,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [createLocation, { isLoading: isCreating }] = useCreateLocationMutation();
  const [updateLocation, { isLoading: isUpdating }] = useUpdateLocationMutation();
  // Delete not supported by backend; confirm dialog shows info message
  const [bulkCreateLocations, { isLoading: isBulking }] = useBulkCreateLocationsMutation();

  const saving = isCreating || isUpdating;

  const showEmpty = !isLoading && rows.length === 0;

  // ------------------
  // Actions
  // ------------------
  const onOpenCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      type: "hotel",
      city: null,
      country: null,
      address_line1: null,
      region: null,
      lat: null,
      lng: null,
      notes: null,
    });
    setOpenForm(true);
  };

  const onOpenEdit = (l: Location) => {
    setEditing(l);
    setForm({
      name: l.name ?? "",
      type: l.type,
      city: l.city ?? null,
      country: l.country ?? null,
      address_line1: l.address_line1 ?? null,
      region: l.region ?? null,
      lat: (l.lat as any) ?? null,
      lng: (l.lng as any) ?? null,
      notes: l.notes ?? null,
    });
    setOpenForm(true);
  };

  const onOpenView = (l: Location) => {
    setViewing(l);
    setOpenView(true);
  };

  const onSubmitForm = async () => {
    try {
      if (!form.name.trim()) return;

      const payload: LocationCreate = {
        ...form,
        name: form.name.trim(),
        city: form.city || null,
        country: form.country || null,
        address_line1: form.address_line1 || null,
        region: form.region || null,
        notes: form.notes || null,
        lat: form.lat ?? null,
        lng: form.lng ?? null,
      };

      if (!editing) {
        await createLocation(payload).unwrap();
        enqueueSnackbar("Location created", { variant: "success" });
      } else {
        await updateLocation({ id: editing.id, patch: payload }).unwrap();
        enqueueSnackbar("Location updated", { variant: "success" });
      }

      setOpenForm(false);
      setEditing(null);
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to save location";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setOpenDelete(false);
    setDeleteTarget(null);
    setPage(1);
    enqueueSnackbar("Deleting locations is not supported.", { variant: "info" });
  };

  // ------------------
  // Bulk create
  // Format: name|type|city|country|address
  // ------------------
  const parseBulk = () => {
    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    return lines.map((line) => {
      const [name, typeRaw, city, country, address_line1] = line
        .split("|")
        .map((x) => (x || "").trim());

      const t = (typeRaw || "other") as LocationType;

      return {
        name,
        type: (LOCATION_TYPES.some((x) => x.value === t) ? t : "other") as LocationType,
        city: city || null,
        country: country || null,
        address_line1: address_line1 || null,
      };
    });
  };

  const bulkPreview = useMemo(
    () =>
      bulkText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((line) => {
          const [name, typeRaw, city, country, address_line1] = line.split("|").map((x) => (x || "").trim());
          const t = (typeRaw || "other") as LocationType;
          return {
            name,
            type: (LOCATION_TYPES.some((x) => x.value === t) ? t : "other") as LocationType,
            city: city || null,
            country: country || null,
            address_line1: address_line1 || null,
          };
        }),
    [bulkText]
  );

  const onBulkCreate = async () => {
    try {
      const items = parseBulk().filter((x) => x.name?.trim());
      if (!items.length) return;

      await bulkCreateLocations({ items }).unwrap();
      enqueueSnackbar(`Created ${items.length} locations`, { variant: "success" });

      setOpenBulk(false);
      setBulkText("");
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Bulk create failed";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Locations"
        subtitle="Hotels, airports, ports, stops"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setOpenBulk(true)}
            >
              Bulk create
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={onOpenCreate}
            >
              New Location
            </Button>
          </Stack>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Type"
          select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value as LocationType | "" })}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {LOCATION_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="City"
          value={city}
          onChange={(e) => setFilters({ city: e.target.value })}
          size="small"
          sx={{ minWidth: 160 }}
          placeholder="Filter by city"
        />
        <TextField
          label="Country"
          value={country}
          onChange={(e) => setFilters({ country: e.target.value })}
          size="small"
          sx={{ minWidth: 120 }}
          placeholder="e.g. GR"
        />
        <TextField
          label="Search (name, address, city, region)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 220, flex: 1 }}
        />
      </FilterBar>

      {error ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          Failed to load locations.
        </Paper>
      ) : showEmpty ? (
        <EmptyState
          title="No locations yet"
          description="Locations are required for transfer stops."
          actionLabel="Create location"
          onAction={onOpenCreate}
        />
      ) : (
        <>
          <Paper variant="outlined" sx={{ overflow: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={toggleSort}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Name{sortIcon()}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={typeLabel(r.type)} />
                    </TableCell>
                    <TableCell>{r.city || "-"}</TableCell>
                    <TableCell>{r.country || "-"}</TableCell>
                    <TableCell>{r.address_line1 || "-"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenView(r)} aria-label="view">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onOpenEdit(r)} aria-label="edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {/* <IconButton size="small" onClick={() => onAskDelete(r)} aria-label="delete">
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton> */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* paging */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: {count} • Page: {page}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={isFetching || !canPrev}
              >
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <ViewModal
        open={openView}
        onClose={() => setOpenView(false)}
        title="Location details"
        subtitle={viewing ? `${viewing.name} · ${typeLabel(viewing.type)}` : undefined}
        rows={
          viewing
            ? [
                { label: "City", value: viewing.city },
                { label: "Country", value: viewing.country },
                { label: "Region", value: viewing.region },
                { label: "Address", value: viewing.address_line1 },
                {
                  label: "Lat / Lng",
                  value: [viewing.lat, viewing.lng].every((v) => v != null) ? `${viewing.lat} / ${viewing.lng}` : null,
                },
                { label: "Notes", value: viewing.notes },
              ]
            : undefined
        }
        onEdit={
          viewing
            ? () => {
                onOpenEdit(viewing);
                setOpenView(false);
              }
            : undefined
        }
      />

      <FormModal
        open={openForm}
        onClose={() => setOpenForm(false)}
        title={editing ? "Edit location" : "New location"}
        onSubmit={onSubmitForm}
        submitDisabled={!form.name.trim()}
        loading={saving}
        maxWidth="sm"
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Airport, Hotel name"
              required
              autoFocus
              fullWidth
            />
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as LocationType }))}
              fullWidth
            >
              {LOCATION_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="City (optional)"
              value={form.city ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, city: e.target.value || null }))}
              placeholder="City"
              fullWidth
            />
            <TextField
              label="Country (optional)"
              value={form.country ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, country: e.target.value || null }))}
              placeholder="e.g. GR"
              fullWidth
            />
          </Stack>
          <TextField
            label="Address line 1 (optional)"
            value={form.address_line1 ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, address_line1: e.target.value || null }))}
            placeholder="Street, number"
            fullWidth
          />
          <TextField
            label="Region (optional)"
            value={form.region ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, region: e.target.value || null }))}
            placeholder="State / region"
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Lat (optional)"
              type="number"
              value={form.lat ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, lat: e.target.value ? Number(e.target.value) : null }))}
              placeholder="e.g. 37.98"
              fullWidth
              inputProps={{ step: "any" }}
            />
            <TextField
              label="Lng (optional)"
              type="number"
              value={form.lng ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, lng: e.target.value ? Number(e.target.value) : null }))}
              placeholder="e.g. 23.73"
              fullWidth
              inputProps={{ step: "any" }}
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            value={form.notes ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value || null }))}
            placeholder="Internal notes"
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormModal>

      {/* Bulk dialog */}
      <Dialog open={openBulk} onClose={() => setOpenBulk(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk create locations</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Format: one per line → <b>name|type|city|country|address</b>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Example: <b>Agapi Beach Hotel|hotel|Heraklion|GR|Ammoudara</b>
          </Typography>

          <TextField
            multiline
            minRows={8}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            placeholder="Paste lines here..."
          />

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Preview (first 5)</Typography>
            <Typography variant="body2" color="text.secondary">
              Parsed: {bulkPreview.filter((x) => x.name?.trim()).length}
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: "background.default" }}>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(bulkPreview.slice(0, 5), null, 2)}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulk(false)} disabled={isBulking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onBulkCreate}
            disabled={isBulking || !bulkText.trim()}
          >
            {isBulking ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={openDelete}
        title="Delete location?"
        description={deleteTarget ? `This will delete "${deleteTarget.name}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={false}
        onClose={() => {
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
}
